import type { BlockType } from '@/api/courses';
import { uploadsApi, type UploadCategory } from '@/api/uploads';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/app/i18n';
import { cn } from '@/lib/utils';
import { sanitizeHtml } from '@/lib/sanitizeHtml';
import { titleFromEditor, titleHtml } from '@/lib/blockTitle';
import { tfNotGivenAllowed } from '@/lib/trueFalse';
import { addAlternativeAt } from '@/lib/gapTemplateEdit';
import { TableFillEditor } from './TableFillEditor';
import { OpenItemsEditor } from './OpenItemsEditor';
import {
  listAtSelection,
  normalizeListNesting,
  OL_STYLES,
  selectionInListItem,
  setListStyle,
  UL_STYLES,
  type ListStyle,
} from '@/lib/richTextLists';

/**
 * Block registry — declarative metadata for the 26+ content block types
 * required by the lesson constructor (ТЗ §3.2). Each entry describes:
 *   - icon: Material Symbol name shown in the palette
 *   - labelKey: translation key of the block name
 *   - category: groups blocks in the palette (palette grouping per ТЗ)
 *   - initialContent: default JSON shape persisted in `blocks.content`
 *   - renderEditor: how the block is rendered inside the builder canvas
 *
 * Adding a new block type = add a row here. The builder itself doesn't need
 * to know about specific types beyond this registry.
 */

export type BlockCategory =
  | 'media' // изображения, аудио, видео
  | 'fill' // слова и пропуски
  | 'quiz' // тесты, true/false
  | 'order' // порядок, matching
  | 'text' // тексты, эссе
  | 'misc'; // словарь, callout, ссылка, разделитель

export type BlockDef = {
  type: BlockType;
  /**
   * Плитка палитры, создающая тот же тип с другим начальным содержимым (вариант).
   * Без `key` плитка — сам тип; редактор и поиск по типу берут только такие записи.
   * Пример — «True / False»: `true_false_ng` с `not_given: false` (13.10.2026).
   */
  key?: string;
  /** Ключ перевода названия блока — сам текст живёт в locales. */
  labelKey: string;
  icon: string;
  category: BlockCategory;
  initialContent: () => Record<string, unknown>;
  renderEditor: (content: Record<string, unknown>, onChange: (next: Record<string, unknown>) => void) => ReactNode;
};

const SimpleTextEdit = ({
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) => (
  <textarea
    rows={rows}
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full resize-none rounded-button bg-surface-container-low p-3 text-on-surface focus:outline-none"
  />
);

// Brand-ish text colours offered in the rich editor (on-surface, primary, and a
// few clear semantic accents). Kept short so the toolbar stays compact.
const RTE_COLORS = ['#1B2130', '#FF941F', '#3B82F6', '#16A34A', '#DC2626'];
// Soft highlight (background) fills — light tints so dark text stays readable on
// top. 'transparent' acts as "no fill" (removes an existing highlight).
const RTE_HILITES = ['transparent', '#FEF08A', '#BBF7D0', '#BFDBFE', '#FBCFE8', '#FED7AA'];

const RTEBtn = ({ onClick, title, children }: { onClick: () => void; title: string; children: ReactNode }) => (
  <button
    type="button"
    title={title}
    // preventDefault on mousedown keeps the editor's text selection intact when a
    // toolbar button is pressed (otherwise the click blurs it and the command
    // applies to nothing).
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className="flex h-7 min-w-7 items-center justify-center rounded px-1.5 text-sm text-on-surface hover:bg-surface-container-high"
  >
    {children}
  </button>
);

// Значки списков — своим SVG: в урезанном шрифте Material их нет (сторож iconSubset).
const BulletListGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="4.5" cy="7" r="1" /><circle cx="4.5" cy="12" r="1" /><circle cx="4.5" cy="17" r="1" />
    <path d="M9 7h11M9 12h11M9 17h11" />
  </svg>
);
const NumberListGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 7h10M10 12h10M10 17h10M4 5l1.5-1v5M3.5 13.2c0-.9.7-1.4 1.5-1.4s1.5.5 1.5 1.3c0 1.1-3 1.9-3 3.4h3" />
  </svg>
);
// Образец стиля в меню — сами знаки, переводить нечего.
const LIST_STYLE_SAMPLE: Record<ListStyle, string> = {
  disc: '●', circle: '○', square: '■',
  decimal: '1.', 'lower-alpha': 'a.', 'upper-alpha': 'A.', 'lower-roman': 'i.', 'upper-roman': 'I.',
};

// RichTextEditor — WYSIWYG contentEditable field. Formatting is applied LIVE:
// ⌘/Ctrl+B/I/U work natively in a contentEditable, and the toolbar adds colour.
// The author always sees styled text, never raw HTML tags. Stores sanitized HTML
// (rendered later via textToHtml→sanitizeHtml, same pipeline as the Text block).
export const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  minRows = 3,
  singleLine = false,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minRows?: number;
  /**
   * Заголовок задания (D39): одна строка. Enter не делает абзаца, вставка — простым
   * текстом, кнопки и сочетания «Жирный» нет — заголовок у ученика и так 700
   * (решение владельца 13.09.2026). Без форматирования сохраняется простой текст,
   * а не HTML с `&amp;` — см. `lib/blockTitle.ts`.
   */
  singleLine?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  // Старый заголовок — простой текст с `&` и `<`: в поле его кладём экранированным,
  // иначе «a < b» разобралось бы как тег и пропало.
  const shown = singleLine ? titleHtml(value) : (value ?? '');

  // Seed the DOM from `value` only when it changes from the OUTSIDE (loading a
  // block, undo) — never while the author is typing, or the caret would jump.
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== shown) {
      el.innerHTML = shown;
    }
  }, [shown]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    // Списки (13.10.2026): вложенный <ul> из Tab — внутрь пункта, см. lib/richTextLists.ts.
    onChange(singleLine ? titleFromEditor(el.innerHTML, el.textContent ?? '') : sanitizeHtml(normalizeListNesting(el.innerHTML)));
  };

  // Меню стиля маркера / нумерации: какое открыто.
  const [listMenu, setListMenu] = useState<'ul' | 'ol' | null>(null);
  const applyListStyle = (kind: 'ul' | 'ol', style: ListStyle) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    let list = listAtSelection(el);
    // Курсор не в списке нужного вида — сначала сделать список, потом стиль.
    if (!list || list.nodeName.toLowerCase() !== kind) {
      document.execCommand(kind === 'ul' ? 'insertUnorderedList' : 'insertOrderedList');
      list = listAtSelection(el);
    }
    if (list) setListStyle(list, style);
    setListMenu(null);
    emit();
  };

  const apply = (cmd: string, arg?: string) => {
    ref.current?.focus();
    // Colour commands must emit CSS (<span style>) rather than deprecated
    // <font>/attributes, so sanitizeHtml's style whitelist keeps them.
    if (cmd === 'foreColor' || cmd === 'hiliteColor') document.execCommand('styleWithCSS', false, 'true');
    document.execCommand(cmd, false, arg);
    emit();
  };

  return (
    <div className="overflow-hidden rounded-button bg-surface-container-low">
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5">
        {!singleLine && <RTEBtn onClick={() => apply('bold')} title={i18n.t('blockRegistry.rteBold')}><b>B</b></RTEBtn>}
        <RTEBtn onClick={() => apply('italic')} title={i18n.t('blockRegistry.rteItalic')}><i>I</i></RTEBtn>
        <RTEBtn onClick={() => apply('underline')} title={i18n.t('blockRegistry.rteUnderline')}><u>U</u></RTEBtn>
        <span className="mx-1 h-4 w-px bg-outline-variant/40" />
        {RTE_COLORS.map((col) => (
          <button
            key={col}
            type="button"
            title={i18n.t('blockRegistry.rteColor')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => apply('foreColor', col)}
            className="h-5 w-5 rounded-full border border-black/10"
            style={{ backgroundColor: col }}
          />
        ))}
        <span className="mx-1 h-4 w-px bg-outline-variant/40" />
        {/* Highlight / fill — square swatches to distinguish from the round text
            colours. The first (transparent) removes an existing highlight. */}
        {RTE_HILITES.map((col) => (
          <button
            key={col}
            type="button"
            title={col === 'transparent' ? i18n.t('blockRegistry.rteNoFill') : i18n.t('blockRegistry.rteFill')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => apply('hiliteColor', col)}
            className="flex h-5 w-5 items-center justify-center rounded border border-black/10"
            style={{ backgroundColor: col === 'transparent' ? undefined : col }}
          >
            {col === 'transparent' && (
              <span className="material-symbols-outlined text-[14px] text-on-surface-variant">format_color_reset</span>
            )}
          </button>
        ))}
        {!singleLine && (
          <>
            <span className="mx-1 h-4 w-px bg-outline-variant/40" />
            {(['ul', 'ol'] as const).map((kind) => (
              <span key={kind} className="relative flex items-center">
                <RTEBtn
                  onClick={() => apply(kind === 'ul' ? 'insertUnorderedList' : 'insertOrderedList')}
                  title={i18n.t(kind === 'ul' ? 'blockRegistry.rteBulleted' : 'blockRegistry.rteNumbered')}
                >
                  {kind === 'ul' ? <BulletListGlyph /> : <NumberListGlyph />}
                </RTEBtn>
                <RTEBtn onClick={() => setListMenu((m) => (m === kind ? null : kind))} title={i18n.t('blockRegistry.rteListStyle')}>
                  <span className="material-symbols-outlined text-[14px]">expand_more</span>
                </RTEBtn>
                {listMenu === kind && (
                  <span className="absolute left-0 top-8 z-20 flex gap-1 rounded-button border border-outline-variant/50 bg-surface p-1 shadow-ambient">
                    {(kind === 'ul' ? UL_STYLES : OL_STYLES).map((s) => (
                      <RTEBtn key={s} onClick={() => applyListStyle(kind, s)} title={i18n.t('blockRegistry.rteListStyle')}>
                        {LIST_STYLE_SAMPLE[s]}
                      </RTEBtn>
                    ))}
                  </span>
                )}
              </span>
            ))}
          </>
        )}
        <span className="mx-1 h-4 w-px bg-outline-variant/40" />
        <RTEBtn onClick={() => apply('removeFormat')} title={i18n.t('blockRegistry.rteClear')}>
          <span className="material-symbols-outlined text-[16px]">format_clear</span>
        </RTEBtn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onKeyDown={
          singleLine
            ? (e) => {
                // Заголовок — одна строка, и жирного у него нет: ⌘/Ctrl+B в
                // contentEditable работает сам по себе, без кнопки на панели.
                if (e.key === 'Enter') e.preventDefault();
                if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') e.preventDefault();
              }
            : (e) => {
                // Tab / Shift+Tab в пункте списка — вложить / вынести, как в Word.
                // Вне списка Tab по-прежнему уводит фокус к следующему полю.
                if (e.key === 'Tab' && ref.current && selectionInListItem(ref.current)) {
                  e.preventDefault();
                  apply(e.shiftKey ? 'outdent' : 'indent');
                }
              }
        }
        onPaste={
          singleLine
            ? (e) => {
                // Вставка из Word принесла бы абзацы, шрифты и белую заливку —
                // в заголовок кладём только текст одной строкой.
                e.preventDefault();
                document.execCommand('insertText', false, e.clipboardData.getData('text/plain').replace(/\s+/g, ' '));
              }
            : undefined
        }
        data-placeholder={placeholder}
        style={{ minHeight: `${minRows * 1.6}rem` }}
        className="rt px-3 pb-3 pt-1 text-on-surface outline-none [&_p]:my-1 empty:before:text-on-surface-variant empty:before:content-[attr(data-placeholder)]"
      />
    </div>
  );
};

const SimpleInput = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) => (
  <input
    type="text"
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    className="w-full rounded-button bg-surface-container-low p-3 text-on-surface focus:outline-none"
  />
);

// ---- Structured quiz editor (Edvibe-style) ----
// Options are fields, each with a "correct" checkbox — no fragile numeric answer
// index, and MULTIPLE correct options are allowed. Reads the legacy shape
// ({options: string[], answer: index}) and migrates it on first edit. Kept in
// sync with normalizeQuizOptions in the student renderer.
type QuizOpt = { text: string; correct: boolean };
function toQuizOptions(c: Record<string, unknown>): QuizOpt[] {
  const raw = c.options;
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'object') {
    return (raw as { text?: string; correct?: boolean }[]).map((o) => ({ text: String(o?.text ?? ''), correct: !!o?.correct }));
  }
  const legacy = (raw as string[] | undefined) ?? [];
  const ans = typeof c.answer === 'number' ? (c.answer as number) : -1;
  const opts = legacy.map((text, i) => ({ text, correct: i === ans }));
  return opts.length ? opts : [{ text: '', correct: false }, { text: '', correct: false }];
}

// Generic "several items in one block" wrapper. Manages content[field] as an
// array of item objects (add / remove / reorder), migrating a legacy flat block
// into a single item on first edit. Each item is drawn by renderItem, which gets
// the item and an item-setter. Keeps blocks backward-compatible: an untouched
// legacy block stays flat until the admin edits it.
type Item = Record<string, unknown>;
const RepeatableEditor = ({
  content,
  onChange,
  field,
  legacyKeys,
  toItem,
  emptyItem,
  addLabel,
  itemLabel,
  renderItem,
  reorder = false,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
  field: string;
  legacyKeys: string[];
  toItem: (c: Record<string, unknown>) => Item;
  emptyItem: () => Item;
  addLabel: string;
  itemLabel: (index: number, total: number) => string;
  renderItem: (item: Item, setItem: (n: Item) => void) => ReactNode;
  reorder?: boolean;
}) => {
  const { t } = useTranslation();
  const raw = content[field];
  const items: Item[] = Array.isArray(raw) && raw.length > 0 ? (raw as Item[]) : [toItem(content)];
  const setItems = (next: Item[]) => {
    const rest = { ...content };
    legacyKeys.forEach((k) => delete rest[k]);
    onChange({ ...rest, [field]: next });
  };
  const setItem = (i: number, n: Item) => setItems(items.map((x, j) => (j === i ? n : x)));
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="space-y-2 rounded-xl border border-outline-variant/60 p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
              {itemLabel(i, items.length)}
            </span>
            <div className="flex items-center gap-0.5">
              {reorder && (
                <>
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="↑" className="rounded-button p-1 text-on-surface-variant hover:text-primary disabled:opacity-30">
                    <span className="material-symbols-outlined text-sm">arrow_upward</span>
                  </button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} title="↓" className="rounded-button p-1 text-on-surface-variant hover:text-primary disabled:opacity-30">
                    <span className="material-symbols-outlined text-sm">arrow_downward</span>
                  </button>
                </>
              )}
              <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} disabled={items.length === 1} title={t('common.delete')} className="rounded-button p-1 text-on-surface-variant hover:text-error disabled:opacity-30">
                <span className="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
          </div>
          {renderItem(item, (n) => setItem(i, n))}
        </div>
      ))}
      <button type="button" onClick={() => setItems([...items, emptyItem()])} className="flex items-center gap-1 text-sm font-medium text-primary">
        <span className="material-symbols-outlined text-base">add</span>
        {addLabel}
      </button>
    </div>
  );
};

// Structured options list (per quiz question): each option has a "correct"
// toggle instead of a fragile numeric answer index.
const QuizOptionsEditor = ({ options, onChange }: { options: QuizOpt[]; onChange: (n: QuizOpt[]) => void }) => {
  const { t } = useTranslation();
  const rows = options.length ? options : [{ text: '', correct: false }, { text: '', correct: false }];
  return (
    <div className="space-y-1.5">
      {rows.map((o, i) => (
        <div key={i} className="flex items-center gap-2">
          <button
            type="button"
            title={t('blockRegistry.markCorrect')}
            onClick={() => onChange(rows.map((x, j) => (j === i ? { ...x, correct: !x.correct } : x)))}
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-button border transition',
              o.correct ? 'border-tertiary bg-tertiary-fixed text-on-tertiary-fixed-variant' : 'border-outline-variant text-transparent hover:border-outline',
            )}
          >
            <span className="material-symbols-outlined text-base">check</span>
          </button>
          <input
            value={o.text}
            onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
            placeholder={t('blockRegistry.optionText')}
            className="flex-1 rounded-button bg-surface-container-low p-2.5 text-sm text-on-surface focus:outline-none"
          />
          <button
            type="button"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
            title={t('common.delete')}
            className="rounded-button p-1 text-on-surface-variant hover:text-error"
          >
            <span className="material-symbols-outlined text-base">close</span>
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...rows, { text: '', correct: false }])}
        className="flex items-center gap-1 text-sm font-medium text-primary"
      >
        <span className="material-symbols-outlined text-base">add</span>
        {t('blockRegistry.addOption')}
      </button>
    </div>
  );
};

const QuizEditor = ({
  content,
  onChange,
  timer,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
  timer?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <RepeatableEditor
        content={content}
        onChange={onChange}
        field="questions"
        legacyKeys={['question', 'options', 'answer', 'explanation']}
        toItem={(c) => ({ text: (c.question as string) ?? '', options: toQuizOptions(c), explanation: (c.explanation as string) ?? '' })}
        emptyItem={() => ({ text: '', options: [{ text: '', correct: false }, { text: '', correct: false }], explanation: '' })}
        addLabel={i18n.t('blockRegistry.addQuestion')}
        itemLabel={(i, total) => (total > 1 ? i18n.t('blockRegistry.itemQuestion', { i: i + 1, total }) : i18n.t('blockRegistry.oneQuestion'))}
        renderItem={(item, setItem) => (
          <div className="space-y-2">
            <SimpleTextEdit
              value={(item.text as string) ?? ''}
              onChange={(v) => setItem({ ...item, text: v })}
              placeholder={t('blockRegistry.quizQuestion')}
            />
            <QuizOptionsEditor options={(item.options as QuizOpt[]) ?? []} onChange={(opts) => setItem({ ...item, options: opts })} />
            <SimpleInput
              value={(item.explanation as string) ?? ''}
              onChange={(v) => setItem({ ...item, explanation: v })}
              placeholder={t('blockRegistry.explanation')}
            />
          </div>
        )}
      />
      {timer && (
        <SimpleInput
          value={String((content.seconds as number) ?? 30)}
          onChange={(v) => onChange({ ...content, seconds: Number(v) || 30 })}
          placeholder={t('blockRegistry.seconds')}
        />
      )}
    </div>
  );
};

type TFStmt = { text: string; answer: 'true' | 'false' | 'not_given' };

// Reads the new {statements:[{text,answer}]} shape and migrates the legacy
// {statement, answer} single-statement shape so old blocks open cleanly.
function toTFStatements(c: Record<string, unknown>): TFStmt[] {
  const norm = (a: unknown): TFStmt['answer'] =>
    a === 'false' || a === 'not_given' ? a : 'true';
  const raw = c.statements;
  if (Array.isArray(raw) && raw.length > 0) {
    return (raw as { text?: string; answer?: string }[]).map((s) => ({
      text: String(s?.text ?? ''),
      answer: norm(s?.answer),
    }));
  }
  return [{ text: String(c.statement ?? ''), answer: norm(c.answer) }];
}

const TrueFalseEditor = ({
  content,
  onChange,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
}) => {
  const { t } = useTranslation();
  const statements = toTFStatements(content);
  const notGiven = tfNotGivenAllowed(content);
  const setStatements = (next: TFStmt[], extra: Record<string, unknown> = {}) => {
    // Drop the legacy single-statement keys — data now lives in `statements`.
    const rest = { ...content };
    delete rest.statement;
    delete rest.answer;
    onChange({ ...rest, ...extra, statements: next });
  };
  // Выключили «Not Given» — такие ответы становятся «False»: сервер отвергает
  // not_given у варианта без него (block_content_guard.go), автосохранение встало бы.
  const toggleNotGiven = (on: boolean) =>
    setStatements(
      on ? statements : statements.map((s) => (s.answer === 'not_given' ? { ...s, answer: 'false' as const } : s)),
      { not_given: on },
    );
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm text-on-surface">
        <input type="checkbox" checked={notGiven} onChange={(e) => toggleNotGiven(e.target.checked)} />
        {t('blockRegistry.tfNotGivenToggle')}
      </label>
      <div className="space-y-2">
        {statements.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={s.text}
              onChange={(e) => setStatements(statements.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
              placeholder={t('blockRegistry.tfStatement')}
              className="flex-1 rounded-button bg-surface-container-low p-2.5 text-sm text-on-surface focus:outline-none"
            />
            <select
              value={s.answer}
              onChange={(e) => setStatements(statements.map((x, j) => (j === i ? { ...x, answer: e.target.value as TFStmt['answer'] } : x)))}
              className="rounded-button bg-surface-container-low p-2.5 text-sm text-on-surface focus:outline-none"
            >
              <option value="true">True</option>
              <option value="false">False</option>
              {notGiven && <option value="not_given">Not Given</option>}
            </select>
            <button
              type="button"
              onClick={() => setStatements(statements.filter((_, j) => j !== i))}
              title={t('common.delete')}
              className="rounded-button p-1 text-on-surface-variant hover:text-error"
            >
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setStatements([...statements, { text: '', answer: 'true' }])}
        className="flex items-center gap-1 text-sm font-medium text-primary"
      >
        <span className="material-symbols-outlined text-base">add</span>
        {t('blockRegistry.addStatement')}
      </button>
      <SimpleInput
        value={(content.explanation as string) ?? ''}
        onChange={(v) => onChange({ ...content, explanation: v })}
        placeholder={t('blockRegistry.explanation')}
      />
    </div>
  );
};

type MatchPair = { left: string; right: string };

// Edvibe matching = two-column pair rows. Reads the structured `pairs` array.
const MatchingEditor = ({
  content,
  onChange,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
}) => {
  const { t } = useTranslation();
  const pairs = ((content.pairs as MatchPair[]) ?? []).map((p) => ({
    left: String(p?.left ?? ''),
    right: String(p?.right ?? ''),
  }));
  const rows = pairs.length ? pairs : [{ left: '', right: '' }];
  const setPairs = (next: MatchPair[]) => {
    const rest = { ...content };
    delete rest.pairsRaw;
    onChange({ ...rest, pairs: next });
  };
  return (
    <div className="space-y-2">
      <div className="space-y-2">
        {rows.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={p.left}
              onChange={(e) => setPairs(rows.map((x, j) => (j === i ? { ...x, left: e.target.value } : x)))}
              placeholder={t('blockRegistry.matchLeft')}
              className="flex-1 rounded-button bg-surface-container-low p-2.5 text-sm text-on-surface focus:outline-none"
            />
            <span className="material-symbols-outlined text-on-surface-variant">sync_alt</span>
            <input
              value={p.right}
              onChange={(e) => setPairs(rows.map((x, j) => (j === i ? { ...x, right: e.target.value } : x)))}
              placeholder={t('blockRegistry.matchRight')}
              className="flex-1 rounded-button bg-surface-container-low p-2.5 text-sm text-on-surface focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setPairs(rows.filter((_, j) => j !== i))}
              title={t('common.delete')}
              className="rounded-button p-1 text-on-surface-variant hover:text-error"
            >
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setPairs([...rows, { left: '', right: '' }])}
        className="flex items-center gap-1 text-sm font-medium text-primary"
      >
        <span className="material-symbols-outlined text-base">add</span>
        {t('blockRegistry.addPair')}
      </button>
    </div>
  );
};

type SortColumn = { label: string; items: string[] };

// Edvibe column-sort = named columns each holding items. Reads `columns`.
const ColumnSortEditor = ({
  content,
  onChange,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
}) => {
  const { t } = useTranslation();
  const columns = ((content.columns as SortColumn[]) ?? []).map((c) => ({
    label: String(c?.label ?? ''),
    items: Array.isArray(c?.items) ? c.items.map(String) : [],
  }));
  const rows = columns.length ? columns : [{ label: '', items: [] as string[] }];
  const setColumns = (next: SortColumn[]) => {
    const rest = { ...content };
    delete rest.columnsRaw;
    onChange({ ...rest, columns: next });
  };
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {rows.map((col, i) => (
          <div key={i} className="space-y-2 rounded-card border border-outline-variant p-3">
            <div className="flex items-center gap-2">
              <input
                value={col.label}
                onChange={(e) => setColumns(rows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                placeholder={t('blockRegistry.columnLabel')}
                className="flex-1 rounded-button bg-surface-container-low p-2 text-sm font-medium text-on-surface focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setColumns(rows.filter((_, j) => j !== i))}
                title={t('common.delete')}
                className="rounded-button p-1 text-on-surface-variant hover:text-error"
              >
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            </div>
            <div className="space-y-1.5">
              {col.items.map((it, k) => (
                <div key={k} className="flex items-center gap-1.5">
                  <input
                    value={it}
                    onChange={(e) =>
                      setColumns(rows.map((x, j) => (j === i ? { ...x, items: x.items.map((y, m) => (m === k ? e.target.value : y)) } : x)))
                    }
                    placeholder={t('blockRegistry.columnItem')}
                    className="flex-1 rounded-button bg-surface-container-low p-2 text-sm text-on-surface focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setColumns(rows.map((x, j) => (j === i ? { ...x, items: x.items.filter((_, m) => m !== k) } : x)))}
                    title={t('common.delete')}
                    className="rounded-button p-1 text-on-surface-variant hover:text-error"
                  >
                    <span className="material-symbols-outlined text-sm">close</span>
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setColumns(rows.map((x, j) => (j === i ? { ...x, items: [...x.items, ''] } : x)))}
                className="flex items-center gap-1 text-xs font-medium text-primary"
              >
                <span className="material-symbols-outlined text-sm">add</span>
                {t('blockRegistry.addItem')}
              </button>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setColumns([...rows, { label: '', items: [] }])}
        className="flex items-center gap-1 text-sm font-medium text-primary"
      >
        <span className="material-symbols-outlined text-base">add</span>
        {t('blockRegistry.addColumn')}
      </button>
    </div>
  );
};

// Edvibe "build the sentence" = ordered word tokens. Reads the `words` array
// (the correct order); the student renderer shuffles them.
const WordTokensEditor = ({
  content,
  onChange,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
}) => {
  const { t } = useTranslation();
  const words = ((content.words as string[]) ?? []).map(String);
  const rows = words.length ? words : [''];
  const setWords = (next: string[]) => {
    const rest = { ...content };
    delete rest.wordsRaw;
    onChange({ ...rest, words: next });
  };
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    setWords(next);
  };
  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        {rows.map((w, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <span className="w-5 shrink-0 text-center text-xs text-on-surface-variant">{i + 1}</span>
            <input
              value={w}
              onChange={(e) => setWords(rows.map((x, j) => (j === i ? e.target.value : x)))}
              placeholder={t('blockRegistry.wordToken')}
              className="flex-1 rounded-button bg-surface-container-low p-2 text-sm text-on-surface focus:outline-none"
            />
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="↑" className="rounded-button p-1 text-on-surface-variant hover:text-primary disabled:opacity-30">
              <span className="material-symbols-outlined text-sm">arrow_upward</span>
            </button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} title="↓" className="rounded-button p-1 text-on-surface-variant hover:text-primary disabled:opacity-30">
              <span className="material-symbols-outlined text-sm">arrow_downward</span>
            </button>
            <button type="button" onClick={() => setWords(rows.filter((_, j) => j !== i))} title={t('common.delete')} className="rounded-button p-1 text-on-surface-variant hover:text-error">
              <span className="material-symbols-outlined text-sm">close</span>
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => setWords([...rows, ''])} className="flex items-center gap-1 text-sm font-medium text-primary">
        <span className="material-symbols-outlined text-base">add</span>
        {t('blockRegistry.addWord')}
      </button>
    </div>
  );
};

// Edvibe "order the paragraphs" = ordered sentence/paragraph rows. Reads
// `paragraphs` (the correct order); the student renderer shuffles them.
const SentenceRowsEditor = ({
  content,
  onChange,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
}) => {
  const { t } = useTranslation();
  const paras = ((content.paragraphs as string[]) ?? []).map(String);
  const rows = paras.length ? paras : [''];
  const setParas = (next: string[]) => {
    const rest = { ...content };
    delete rest.paragraphsRaw;
    onChange({ ...rest, paragraphs: next });
  };
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    setParas(next);
  };
  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        {rows.map((p, i) => (
          <div key={i} className="flex items-start gap-1.5">
            <span className="mt-2 w-5 shrink-0 text-center text-xs text-on-surface-variant">{i + 1}</span>
            <textarea
              value={p}
              rows={2}
              onChange={(e) => setParas(rows.map((x, j) => (j === i ? e.target.value : x)))}
              placeholder={t('blockRegistry.sentenceRow')}
              className="flex-1 rounded-button bg-surface-container-low p-2 text-sm text-on-surface focus:outline-none"
            />
            <div className="flex flex-col">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="↑" className="rounded-button p-1 text-on-surface-variant hover:text-primary disabled:opacity-30">
                <span className="material-symbols-outlined text-sm">arrow_upward</span>
              </button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} title="↓" className="rounded-button p-1 text-on-surface-variant hover:text-primary disabled:opacity-30">
                <span className="material-symbols-outlined text-sm">arrow_downward</span>
              </button>
            </div>
            <button type="button" onClick={() => setParas(rows.filter((_, j) => j !== i))} title={t('common.delete')} className="mt-1 rounded-button p-1 text-on-surface-variant hover:text-error">
              <span className="material-symbols-outlined text-sm">close</span>
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => setParas([...rows, ''])} className="flex items-center gap-1 text-sm font-medium text-primary">
        <span className="material-symbols-outlined text-base">add</span>
        {t('blockRegistry.addSentence')}
      </button>
    </div>
  );
};

type VocabWord = { word: string; translation: string; example?: string };

// Edvibe word-set = word / translation (+ optional example) rows, with a TTS
// voice selector for the whole set. Reads `words` + `voice`.
const WordCardSetEditor = ({
  content,
  onChange,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
}) => {
  const { t } = useTranslation();
  const words = ((content.words as VocabWord[]) ?? []).map((w) => ({
    word: String(w?.word ?? ''),
    translation: String(w?.translation ?? ''),
    example: String(w?.example ?? ''),
  }));
  const rows = words.length ? words : [{ word: '', translation: '', example: '' }];
  const setWords = (next: VocabWord[]) => {
    const rest = { ...content };
    delete rest.wordsRaw;
    onChange({ ...rest, words: next });
  };
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm text-on-surface-variant">
        <span className="material-symbols-outlined text-base">volume_up</span>
        {t('blockRegistry.ttsVoice')}
        <select
          value={(content.voice as string) ?? 'en-US'}
          onChange={(e) => onChange({ ...content, voice: e.target.value })}
          className="rounded-button bg-surface-container-low p-2 text-sm text-on-surface focus:outline-none"
        >
          <option value="en-US">English (US)</option>
          <option value="en-GB">English (UK)</option>
          <option value="ru-RU">{i18n.t('blockRegistry.voiceRu')}</option>
          <option value="kk-KZ">Қазақша</option>
        </select>
      </label>
      <div className="space-y-2">
        {rows.map((w, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={w.word}
              onChange={(e) => setWords(rows.map((x, j) => (j === i ? { ...x, word: e.target.value } : x)))}
              placeholder={t('blockRegistry.vocabWord')}
              className="flex-1 rounded-button bg-surface-container-low p-2 text-sm font-medium text-on-surface focus:outline-none"
            />
            <input
              value={w.translation}
              onChange={(e) => setWords(rows.map((x, j) => (j === i ? { ...x, translation: e.target.value } : x)))}
              placeholder={t('blockRegistry.vocabTranslation')}
              className="flex-1 rounded-button bg-surface-container-low p-2 text-sm text-on-surface focus:outline-none"
            />
            <input
              value={w.example ?? ''}
              onChange={(e) => setWords(rows.map((x, j) => (j === i ? { ...x, example: e.target.value } : x)))}
              placeholder={t('blockRegistry.vocabExample')}
              className="flex-1 rounded-button bg-surface-container-low p-2 text-sm text-on-surface-variant focus:outline-none"
            />
            <button type="button" onClick={() => setWords(rows.filter((_, j) => j !== i))} title={t('common.delete')} className="rounded-button p-1 text-on-surface-variant hover:text-error">
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => setWords([...rows, { word: '', translation: '', example: '' }])} className="flex items-center gap-1 text-sm font-medium text-primary">
        <span className="material-symbols-outlined text-base">add</span>
        {t('blockRegistry.addVocab')}
      </button>
    </div>
  );
};

// Fill-family answer options: a list of option rows, each with a "correct"
// radio (sets content.answer to that option's text) + text + remove. Replaces
// the old "CSV options + separate answer field" pair. Reads options[] + answer.
const OptionsWithAnswerEditor = ({
  content,
  onChange,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
}) => {
  const { t } = useTranslation();
  const options = ((content.options as string[]) ?? []).map(String);
  const rows = options.length ? options : ['', ''];
  const answer = (content.answer as string) ?? '';
  const setOptions = (next: string[], nextAnswer?: string) => {
    const rest = { ...content };
    delete rest.optionsRaw;
    onChange({ ...rest, options: next, answer: nextAnswer ?? answer });
  };
  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        {rows.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <button
              type="button"
              title={t('blockRegistry.markCorrect')}
              onClick={() => setOptions(rows, o)}
              disabled={!o}
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition',
                o && answer === o ? 'border-tertiary bg-tertiary-fixed text-on-tertiary-fixed-variant' : 'border-outline-variant text-transparent hover:border-outline',
              )}
            >
              <span className="material-symbols-outlined text-base">check</span>
            </button>
            <input
              value={o}
              onChange={(e) => {
                const val = e.target.value;
                // keep answer pointing at this row if it was the correct one
                setOptions(rows.map((x, j) => (j === i ? val : x)), answer === o ? val : answer);
              }}
              placeholder={t('blockRegistry.optionText')}
              className="flex-1 rounded-button bg-surface-container-low p-2.5 text-sm text-on-surface focus:outline-none"
            />
            <button
              type="button"
              onClick={() => {
                const next = rows.filter((_, j) => j !== i);
                setOptions(next, answer === o ? '' : answer);
              }}
              title={t('common.delete')}
              className="rounded-button p-1 text-on-surface-variant hover:text-error"
            >
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setOptions([...rows, ''])}
        className="flex items-center gap-1 text-sm font-medium text-primary"
      >
        <span className="material-symbols-outlined text-base">add</span>
        {t('blockRegistry.addOption')}
      </button>
    </div>
  );
};

// Flagship multi-gap editor. Admin writes flowing text and marks gaps with
// [[answer]] (or [[answer|alt]] for several accepted answers). A mode selector
// picks how the student fills them (type / dropdown / drag), and distractors
// feed the dropdown & word-bank pools. Content: {template, mode, distractors}.
const GapFillEditor = ({
  content,
  onChange,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
}) => {
  const { t } = useTranslation();
  const template = (content.template as string) ?? '';
  const mode = (content.mode as string) ?? 'input';
  const distractors = ((content.distractors as string[]) ?? []).join(', ');
  const taRef = useRef<HTMLTextAreaElement>(null);

  const insertGap = () => {
    const ta = taRef.current;
    const marker = '[[ответ]]';
    if (!ta) {
      onChange({ ...content, template: template + marker });
      return;
    }
    const start = ta.selectionStart ?? template.length;
    const end = ta.selectionEnd ?? template.length;
    const selected = template.slice(start, end);
    const insert = selected ? `[[${selected}]]` : marker;
    onChange({ ...content, template: template.slice(0, start) + insert + template.slice(end) });
  };

  // «＋ вариант ответа» (13.10.2026): «|» в пропуск под курсором, курсор — после черты.
  const [altMiss, setAltMiss] = useState(false);
  const addAlternative = () => {
    const ta = taRef.current;
    const next = addAlternativeAt(template, ta?.selectionStart ?? -1);
    setAltMiss(next === null);
    if (!next) return;
    onChange({ ...content, template: next.template });
    requestAnimationFrame(() => {
      ta?.focus();
      ta?.setSelectionRange(next.caret, next.caret);
    });
  };

  // Count / split on both gap syntaxes: [[answer|alt]] and Edvibe [a/b*/c]
  // (a single [...] only counts as a gap when it holds a "/" or "*").
  const GAP_RE = /\[\[[^\]]*\]\]|\[[^[\]]*[/*][^[\]]*\]/g;
  const gapCount = (template.match(GAP_RE) ?? []).length;
  const previewParts = template.split(GAP_RE);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={insertGap} className="flex items-center gap-1 text-sm font-medium text-primary">
            <span className="material-symbols-outlined text-base">add_box</span>
            {t('blockRegistry.insertGap')}
          </button>
          <button
            type="button"
            // mousedown не уводит курсор из поля — иначе «пропуск под курсором» терялся бы.
            onMouseDown={(e) => e.preventDefault()}
            onClick={addAlternative}
            className="flex items-center gap-1 text-sm font-medium text-primary"
          >
            <span className="material-symbols-outlined text-base">add</span>
            {t('blockRegistry.addAnswerVariant')}
          </button>
        </div>
        <span className="text-xs text-on-surface-variant">{t('blockRegistry.gapCount', { count: gapCount })}</span>
      </div>
      {altMiss && <p className="text-xs text-on-surface-variant">{t('blockRegistry.addAnswerVariantHint')}</p>}
      <textarea
        ref={taRef}
        rows={4}
        value={template}
        onChange={(e) => onChange({ ...content, template: e.target.value })}
        placeholder={t('blockRegistry.gapTemplatePlaceholder')}
        className="w-full resize-y rounded-button bg-surface-container-low p-3 text-sm text-on-surface focus:outline-none"
      />
      <p className="text-xs text-on-surface-variant">{t('blockRegistry.gapStarHint')}</p>
      {gapCount > 0 && (
        // whitespace-pre-wrap: предпросмотр должен показывать текст ТАК ЖЕ,
        // как его увидит ученик. Без него преподаватель вводит песню по
        // строкам, а в предпросмотре видит слипшийся абзац и думает, что
        // переносы потерялись при сохранении.
        <p className="whitespace-pre-wrap rounded-button bg-surface-container-low p-2 text-sm leading-loose text-on-surface-variant">
          {previewParts.map((part, i) => (
            <span key={i}>
              {part}
              {i < previewParts.length - 1 && (
                <span className="mx-1 inline-block min-w-[3rem] border-b-2 border-outline align-middle" />
              )}
            </span>
          ))}
        </p>
      )}
      <div className="flex items-center gap-2">
        <span className="text-sm text-on-surface-variant">{t('blockRegistry.gapMode')}</span>
        <select
          value={mode}
          onChange={(e) => onChange({ ...content, mode: e.target.value })}
          className="rounded-button bg-surface-container-low p-2 text-sm text-on-surface focus:outline-none"
        >
          <option value="input">{t('blockRegistry.gapModeInput')}</option>
          <option value="dropdown">{t('blockRegistry.gapModeDropdown')}</option>
          <option value="drag">{t('blockRegistry.gapModeDrag')}</option>
        </select>
      </div>
      {mode !== 'input' && (
        <SimpleInput
          value={distractors}
          onChange={(v) => onChange({ ...content, distractors: v.split(',').map((s) => s.trim()).filter(Boolean) })}
          placeholder={t('blockRegistry.gapDistractors')}
        />
      )}
      {mode === 'input' && <CaseSensitiveToggle content={content} onChange={onChange} />}
    </div>
  );
};

/**
 * Флаг «Учитывать регистр» (`case_sensitive`, решение владельца 13.10.2026) — у заданий,
 * где ученик НАБИРАЕТ ответ. По умолчанию выключен: регистр, пробелы и знак на краю не
 * в счёт. Сверяют и фронт, и сервер (lessonAnswerMatch ↔ typedMatch в progress-service).
 */
const CaseSensitiveToggle = ({
  content,
  onChange,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
}) => {
  const { t } = useTranslation();
  return (
    <label className="flex items-center gap-2 text-sm text-on-surface">
      <input
        type="checkbox"
        checked={content.case_sensitive === true}
        onChange={(e) => {
          const next = { ...content };
          if (e.target.checked) next.case_sensitive = true;
          else delete next.case_sensitive;
          onChange(next);
        }}
      />
      {t('blockRegistry.caseSensitive')}
    </label>
  );
};

/** Редактор задания с набором ответа + галочка регистра под ним. */
const WithCaseToggle = ({
  content,
  onChange,
  children,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
  children: ReactNode;
}) => (
  <div className="space-y-3">
    {children}
    <CaseSensitiveToggle content={content} onChange={onChange} />
  </div>
);

/**
 * UploadField — a URL text input PLUS an "upload file" button. On file pick the
 * file is pushed to files-service via uploadsApi.upload(...) and the returned
 * public URL is written into the field (single) or handed to `onUpload` (batch).
 *
 * The bare URL input keeps working so an admin can still paste a URL directly;
 * uploading merely fills it in. The content shape the student renderer reads is
 * untouched — this component only produces a string URL (or a list of URLs).
 */
const UploadField = ({
  value = '',
  onChange,
  onUpload,
  placeholder,
  accept = 'image/*',
  category = 'lesson_media',
  multiple = false,
  showInput = true,
  buttonLabel = i18n.t('blockRegistry.uploadFile'),
}: {
  value?: string;
  onChange?: (v: string) => void;
  onUpload?: (urls: string[]) => void;
  placeholder?: string;
  accept?: string;
  category?: UploadCategory;
  multiple?: boolean;
  showInput?: boolean;
  buttonLabel?: string;
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const results = await Promise.all(files.map((f) => uploadsApi.upload(f, category)));
      const urls = results.map((r) => r.url);
      if (onUpload) onUpload(urls);
      else onChange?.(urls[0]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить файл');
    } finally {
      setUploading(false);
      // reset so picking the same file again still fires onChange
      e.target.value = '';
    }
  };

  const showPreview = showInput && accept.includes('image') && Boolean(value);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {showInput && (
          <input
            type="text"
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange?.(e.target.value)}
            className="w-full rounded-button bg-surface-container-low p-3 text-on-surface focus:outline-none"
          />
        )}
        <label className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-button bg-surface-container-high px-3 py-3 text-sm text-on-surface hover:bg-surface-container-highest">
          <span className="material-symbols-outlined text-base">{uploading ? 'progress_activity' : 'upload'}</span>
          {uploading ? 'Загрузка…' : buttonLabel}
          <input
            type="file"
            accept={accept}
            multiple={multiple}
            disabled={uploading}
            onChange={handleFile}
            className="hidden"
          />
        </label>
      </div>
      {error && <p className="text-sm text-error">{error}</p>}
      {showPreview && <img src={value} alt="" className="max-h-32 rounded-button object-contain" />}
    </div>
  );
};

/**
 * RawField — fixes the "typing gets erased" class of bug in list-style editors.
 *
 * The old editors derived the textarea `value` from a parsed+filtered structure
 * on every render while `onChange` re-parsed the text. Any transient keystroke
 * that produced an empty/label-less line (a fresh newline, a trailing ':' or
 * ',', a lone space) was filtered out, so `value` re-derived to something other
 * than what the user typed and the keystroke was erased instantly.
 *
 * RawField keeps a RAW TEXT buffer (`content[rawKey]`) as the source of truth:
 * the textarea shows exactly what was typed, and the structured field is parsed
 * best-effort alongside it (never fed back into the textarea). `deriveText` is
 * only used when the raw buffer is absent (existing saved blocks).
 */
const RawField = ({
  content,
  onChange,
  rawKey,
  deriveText,
  parse,
  placeholder,
  rows,
  multiline = true,
}: {
  content: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  rawKey: string;
  deriveText: (c: Record<string, unknown>) => string;
  parse: (v: string) => Record<string, unknown>;
  placeholder?: string;
  rows?: number;
  multiline?: boolean;
}) => {
  const value = (content[rawKey] as string) ?? deriveText(content);
  const handle = (v: string) => onChange({ ...content, [rawKey]: v, ...parse(v) });
  return multiline ? (
    <SimpleTextEdit rows={rows} value={value} onChange={handle} placeholder={placeholder} />
  ) : (
    <SimpleInput value={value} onChange={handle} placeholder={placeholder} />
  );
};

// ----- shared parse/derive helpers for list-style blocks -----

const parseImages = (v: string): string[] =>
  v.split('\n').map((s) => s.trim()).filter(Boolean);

const deriveImages = (c: Record<string, unknown>): string =>
  ((c.images as string[]) ?? []).join('\n');

export const blockRegistry: BlockDef[] = [
  // ----- Media -----
  {
    type: 'image',
    labelKey: 'blockRegistry.lblImage',
    icon: 'image',
    category: 'media',
    initialContent: () => ({ url: '', description: '', caption: '' }),
    // ⚠️ ПОРЯДОК ПОЛЕЙ — ТОТ ЖЕ, ЧТО УВИДИТ УЧЕНИК: заголовок (общее поле блока, оно
    // выше по странице) → описание → медиа → подпись. Иначе методист набирает вслепую.
    renderEditor: (c, onChange) => (
      <div className="space-y-2">
        <RichTextEditor value={(c.description as string) ?? ''} onChange={(v) => onChange({ ...c, description: v })} placeholder={i18n.t('blockRegistry.phMediaDescription')} />
        <UploadField value={(c.url as string) ?? ''} onChange={(v) => onChange({ ...c, url: v })} placeholder={i18n.t('blockRegistry.phImageUrl')} accept="image/*" />
        <SimpleInput value={(c.caption as string) ?? ''} onChange={(v) => onChange({ ...c, caption: v })} placeholder={i18n.t('blockRegistry.phCaption')} />
      </div>
    ),
  },
  {
    type: 'image_carousel',
    labelKey: 'blockRegistry.lblCarousel',
    icon: 'collections',
    category: 'media',
    initialContent: () => ({ images: [] as string[] }),
    renderEditor: (c, onChange) => (
      <div className="space-y-2">
        <RawField
          content={c}
          onChange={onChange}
          rawKey="imagesRaw"
          deriveText={deriveImages}
          parse={(v) => ({ images: parseImages(v) })}
          placeholder={i18n.t('blockRegistry.phOneUrlPerLine')}
        />
        <UploadField
          accept="image/*"
          multiple
          showInput={false}
          buttonLabel={i18n.t('blockRegistry.uploadImages')}
          onUpload={(urls) => {
            const currentRaw = (c.imagesRaw as string) ?? deriveImages(c);
            const nextRaw = [currentRaw, ...urls].filter(Boolean).join('\n');
            onChange({ ...c, imagesRaw: nextRaw, images: parseImages(nextRaw) });
          }}
        />
      </div>
    ),
  },
  {
    type: 'gif',
    labelKey: 'blockRegistry.lblGif',
    icon: 'gif',
    category: 'media',
    initialContent: () => ({ url: '' }),
    renderEditor: (c, onChange) => (
      <UploadField value={(c.url as string) ?? ''} onChange={(v) => onChange({ ...c, url: v })} placeholder="URL GIF" accept="image/gif,image/*" />
    ),
  },
  {
    type: 'video',
    labelKey: 'blockRegistry.lblVideo',
    icon: 'play_circle',
    category: 'media',
    initialContent: () => ({ url: '', description: '', caption: '' }),
    // ⚠️ СВОЕГО ПОЛЯ «НАЗВАНИЕ ВИДЕО» БОЛЬШЕ НЕТ. Оно писало в тот же `content.title`,
    // что и общее поле заголовка блока выше по странице: в редакторе стояли два инпута
    // на одно значение, и правка в одном молча перетирала другой.
    renderEditor: (c, onChange) => (
      <div className="space-y-2">
        <RichTextEditor value={(c.description as string) ?? ''} onChange={(v) => onChange({ ...c, description: v })} placeholder={i18n.t('blockRegistry.phMediaDescription')} />
        <UploadField value={(c.url as string) ?? ''} onChange={(v) => onChange({ ...c, url: v })} placeholder="YouTube / Vimeo / S3 URL" accept="video/*" />
        <SimpleInput value={(c.caption as string) ?? ''} onChange={(v) => onChange({ ...c, caption: v })} placeholder={i18n.t('blockRegistry.phCaption')} />
      </div>
    ),
  },
  {
    type: 'audio',
    labelKey: 'blockRegistry.lblAudio',
    icon: 'audiotrack',
    category: 'media',
    initialContent: () => ({ url: '', description: '', caption: '' }),
    renderEditor: (c, onChange) => (
      <div className="space-y-2">
        <RichTextEditor value={(c.description as string) ?? ''} onChange={(v) => onChange({ ...c, description: v })} placeholder={i18n.t('blockRegistry.phMediaDescription')} />
        <UploadField value={(c.url as string) ?? ''} onChange={(v) => onChange({ ...c, url: v })} placeholder="MP3 URL" accept="audio/*" />
        <SimpleInput value={(c.caption as string) ?? ''} onChange={(v) => onChange({ ...c, caption: v })} placeholder={i18n.t('blockRegistry.phCaption')} />
      </div>
    ),
  },
  {
    type: 'audio_record',
    labelKey: 'blockRegistry.lblRecord',
    icon: 'mic',
    category: 'media',
    initialContent: () => ({ instruction: '', max_seconds: 180 }),
    renderEditor: (c, onChange) => (
      <div className="space-y-2">
        <RichTextEditor value={(c.instruction as string) ?? ''} onChange={(v) => onChange({ ...c, instruction: v })} placeholder={i18n.t('blockRegistry.phWhatToRecord')} />
        <SimpleInput value={String((c.max_seconds as number) ?? 180)} onChange={(v) => onChange({ ...c, max_seconds: Number(v) || 180 })} placeholder={i18n.t('blockRegistry.phMaxSeconds')} />
      </div>
    ),
  },

  // ----- Words / fill-in-the-blank -----
  {
    type: 'gap_fill',
    labelKey: 'blockRegistry.lblGaps',
    icon: 'short_text',
    category: 'fill',
    initialContent: () => ({ template: '', mode: 'input', distractors: [] as string[] }),
    renderEditor: (c, onChange) => <GapFillEditor content={c} onChange={onChange} />,
  },
  {
    // «Дополни таблицу» (13.10.2026): ячейки — шаблоны пропусков, lib/tableFill.ts.
    type: 'table_fill',
    labelKey: 'blockRegistry.lblTableFill',
    icon: 'grid_view',
    category: 'fill',
    initialContent: () => ({ header: true, rows: [['', '', ''], ['', '', '']], mode: 'input' }),
    renderEditor: (c, onChange) => <TableFillEditor content={c} onChange={onChange} />,
  },
  {
    type: 'word_drag_to_gap',
    labelKey: 'blockRegistry.lblWordDrag',
    icon: 'drag_indicator',
    category: 'fill',
    initialContent: () => ({ sentence: '', options: ['', ''], answer: '' }),
    renderEditor: (c, onChange) => (
      <RepeatableEditor
        content={c} onChange={onChange} field="items" reorder
        legacyKeys={['sentence', 'options', 'answer']}
        toItem={(x) => ({ sentence: (x.sentence as string) ?? '', options: (x.options as string[]) ?? ['', ''], answer: (x.answer as string) ?? '' })}
        emptyItem={() => ({ sentence: '', options: ['', ''], answer: '' })}
        addLabel={i18n.t('blockRegistry.addSentence2')}
        itemLabel={(i, total) => (total > 1 ? i18n.t('blockRegistry.itemSentence', { i: i + 1, total }) : i18n.t('blockRegistry.oneSentence'))}
        renderItem={(item, setItem) => (
          <div className="space-y-2">
            <SimpleInput value={(item.sentence as string) ?? ''} onChange={(v) => setItem({ ...item, sentence: v })} placeholder={i18n.t('blockRegistry.phSentenceUnderscore')} />
            <OptionsWithAnswerEditor content={item} onChange={setItem} />
          </div>
        )}
      />
    ),
  },
  {
    type: 'word_input_gap',
    labelKey: 'blockRegistry.lblWordType',
    icon: 'edit_note',
    category: 'fill',
    initialContent: () => ({ sentence: '', answer: '' }),
    renderEditor: (c, onChange) => (
      <WithCaseToggle content={c} onChange={onChange}>
      <RepeatableEditor
        content={c} onChange={onChange} field="items" reorder
        legacyKeys={['sentence', 'answer']}
        toItem={(x) => ({ sentence: (x.sentence as string) ?? '', answer: (x.answer as string) ?? '' })}
        emptyItem={() => ({ sentence: '', answer: '' })}
        addLabel={i18n.t('blockRegistry.addSentence2')}
        itemLabel={(i, total) => (total > 1 ? i18n.t('blockRegistry.itemSentence', { i: i + 1, total }) : i18n.t('blockRegistry.oneSentence'))}
        renderItem={(item, setItem) => (
          <div className="space-y-2">
            <SimpleInput value={(item.sentence as string) ?? ''} onChange={(v) => setItem({ ...item, sentence: v })} placeholder={i18n.t('blockRegistry.phSentenceShort')} />
            <SimpleInput value={(item.answer as string) ?? ''} onChange={(v) => setItem({ ...item, answer: v })} placeholder={i18n.t('blockRegistry.phCorrectAnswer')} />
          </div>
        )}
      />
      </WithCaseToggle>
    ),
  },
  {
    type: 'word_form_select',
    labelKey: 'blockRegistry.lblWordForm',
    icon: 'list_alt',
    category: 'fill',
    initialContent: () => ({ word: '', options: ['', ''], answer: '' }),
    renderEditor: (c, onChange) => (
      <RepeatableEditor
        content={c} onChange={onChange} field="items" reorder
        legacyKeys={['word', 'options', 'answer']}
        toItem={(x) => ({ word: (x.word as string) ?? '', options: (x.options as string[]) ?? ['', ''], answer: (x.answer as string) ?? '' })}
        emptyItem={() => ({ word: '', options: ['', ''], answer: '' })}
        addLabel={i18n.t('blockRegistry.addWord2')}
        itemLabel={(i, total) => (total > 1 ? i18n.t('blockRegistry.itemWord', { i: i + 1, total }) : i18n.t('blockRegistry.oneWord'))}
        renderItem={(item, setItem) => (
          <div className="space-y-2">
            <SimpleInput value={(item.word as string) ?? ''} onChange={(v) => setItem({ ...item, word: v })} placeholder={i18n.t('blockRegistry.phBaseWord')} />
            <OptionsWithAnswerEditor content={item} onChange={setItem} />
          </div>
        )}
      />
    ),
  },
  {
    type: 'word_drag_to_image',
    labelKey: 'blockRegistry.lblWordToPic',
    icon: 'photo',
    category: 'fill',
    initialContent: () => ({ image_url: '', options: ['', ''], answer: '' }),
    renderEditor: (c, onChange) => (
      <RepeatableEditor
        content={c} onChange={onChange} field="items" reorder
        legacyKeys={['image_url', 'options', 'answer']}
        toItem={(x) => ({ image_url: (x.image_url as string) ?? '', options: (x.options as string[]) ?? ['', ''], answer: (x.answer as string) ?? '' })}
        emptyItem={() => ({ image_url: '', options: ['', ''], answer: '' })}
        addLabel={i18n.t('blockRegistry.addPicture')}
        itemLabel={(i, total) => (total > 1 ? i18n.t('blockRegistry.itemPicture', { i: i + 1, total }) : i18n.t('blockRegistry.onePicture'))}
        renderItem={(item, setItem) => (
          <div className="space-y-2">
            <UploadField value={(item.image_url as string) ?? ''} onChange={(v) => setItem({ ...item, image_url: v })} placeholder={i18n.t('blockRegistry.phPicUrl')} accept="image/*" />
            <OptionsWithAnswerEditor content={item} onChange={setItem} />
          </div>
        )}
      />
    ),
  },
  {
    type: 'words_to_pictures',
    labelKey: 'blockRegistry.lblWordsBank',
    icon: 'grid_view',
    category: 'fill',
    // Отличие от «Слово к картинке»: там у КАЖДОЙ картинки свой список
    // вариантов, здесь банк ОДИН на все и каждое слово используется ровно
    // один раз. Ученик может рассуждать методом исключения — это другое
    // упражнение, а не другое оформление того же.
    initialContent: () => ({ instruction: '', items: [{ image_url: '', word: '' }], extra_words: [] }),
    renderEditor: (c, onChange) => (
      <div className="space-y-3">
        <SimpleTextEdit
          rows={2}
          value={(c.instruction as string) ?? ''}
          onChange={(v) => onChange({ ...c, instruction: v })}
          placeholder={i18n.t('blockRegistry.phInstructionExample')}
        />
        <RepeatableEditor
          content={c} onChange={onChange} field="items" reorder
          // legacyKeys пуст: блок новый, старого формата «одна картинка на
          // блок» у него не было и переносить нечего.
          legacyKeys={[]}
          toItem={(x) => ({ image_url: (x.image_url as string) ?? '', word: (x.word as string) ?? '' })}
          emptyItem={() => ({ image_url: '', word: '' })}
          addLabel={i18n.t('blockRegistry.addPicture')}
          itemLabel={(i, total) => (total > 1 ? i18n.t('blockRegistry.itemPicture', { i: i + 1, total }) : i18n.t('blockRegistry.onePicture'))}
          renderItem={(item, setItem) => (
            <div className="space-y-2">
              <UploadField
                value={(item.image_url as string) ?? ''}
                onChange={(v) => setItem({ ...item, image_url: v })}
                placeholder={i18n.t('blockRegistry.phPicUrl')}
                accept="image/*"
              />
              <SimpleInput
                value={(item.word as string) ?? ''}
                onChange={(v) => setItem({ ...item, word: v })}
                placeholder={i18n.t('blockRegistry.phWordForPic')}
              />
            </div>
          )}
        />
        {/* Отвлекающие сверх числа картинок: без них последнее слово ученик
            ставит не думая — оно остаётся единственным. */}
        <SimpleInput
          value={((c.extra_words as string[]) ?? []).join(', ')}
          onChange={(v) =>
            onChange({ ...c, extra_words: v.split(',').map((x) => x.trim()).filter(Boolean) })
          }
          placeholder={i18n.t('blockRegistry.phDistractors')}
        />
      </div>
    ),
  },
  {
    type: 'word_input_image',
    labelKey: 'blockRegistry.lblPicCaption',
    icon: 'image_aspect_ratio',
    category: 'fill',
    initialContent: () => ({ image_url: '', answer: '' }),
    renderEditor: (c, onChange) => (
      <WithCaseToggle content={c} onChange={onChange}>
      <RepeatableEditor
        content={c} onChange={onChange} field="items" reorder
        legacyKeys={['image_url', 'answer']}
        toItem={(x) => ({ image_url: (x.image_url as string) ?? '', answer: (x.answer as string) ?? '' })}
        emptyItem={() => ({ image_url: '', answer: '' })}
        addLabel={i18n.t('blockRegistry.addPicture')}
        itemLabel={(i, total) => (total > 1 ? i18n.t('blockRegistry.itemPicture', { i: i + 1, total }) : i18n.t('blockRegistry.onePicture'))}
        renderItem={(item, setItem) => (
          <div className="space-y-2">
            <UploadField value={(item.image_url as string) ?? ''} onChange={(v) => setItem({ ...item, image_url: v })} placeholder={i18n.t('blockRegistry.phPicUrl')} accept="image/*" />
            <SimpleInput value={(item.answer as string) ?? ''} onChange={(v) => setItem({ ...item, answer: v })} placeholder={i18n.t('blockRegistry.phExpectedCaption')} />
          </div>
        )}
      />
      </WithCaseToggle>
    ),
  },
  {
    type: 'word_form_image',
    labelKey: 'blockRegistry.lblPicWordForm',
    icon: 'image_search',
    category: 'fill',
    initialContent: () => ({ image_url: '', options: ['', ''], answer: '' }),
    renderEditor: (c, onChange) => (
      <RepeatableEditor
        content={c} onChange={onChange} field="items" reorder
        legacyKeys={['image_url', 'options', 'answer']}
        toItem={(x) => ({ image_url: (x.image_url as string) ?? '', options: (x.options as string[]) ?? ['', ''], answer: (x.answer as string) ?? '' })}
        emptyItem={() => ({ image_url: '', options: ['', ''], answer: '' })}
        addLabel={i18n.t('blockRegistry.addPicture')}
        itemLabel={(i, total) => (total > 1 ? i18n.t('blockRegistry.itemPicture', { i: i + 1, total }) : i18n.t('blockRegistry.onePicture'))}
        renderItem={(item, setItem) => (
          <div className="space-y-2">
            <UploadField value={(item.image_url as string) ?? ''} onChange={(v) => setItem({ ...item, image_url: v })} placeholder={i18n.t('blockRegistry.phPicUrl')} accept="image/*" />
            <OptionsWithAnswerEditor content={item} onChange={setItem} />
          </div>
        )}
      />
    ),
  },

  // ----- Quiz -----
  {
    type: 'quiz_no_timer',
    labelKey: 'blockRegistry.lblQuiz',
    icon: 'quiz',
    category: 'quiz',
    initialContent: () => ({ question: '', options: [{ text: '', correct: false }, { text: '', correct: false }], explanation: '' }),
    renderEditor: (c, onChange) => <QuizEditor content={c} onChange={onChange} />,
  },
  {
    type: 'quiz_with_timer',
    labelKey: 'blockRegistry.lblQuizTimer',
    icon: 'timer',
    category: 'quiz',
    initialContent: () => ({ question: '', options: [{ text: '', correct: false }, { text: '', correct: false }], explanation: '', seconds: 30 }),
    renderEditor: (c, onChange) => <QuizEditor content={c} onChange={onChange} timer />,
  },
  {
    type: 'true_false_ng',
    labelKey: 'blockRegistry.lblTfng',
    icon: 'rule',
    category: 'quiz',
    initialContent: () => ({ statements: [{ text: '', answer: 'true' }], explanation: '' }),
    renderEditor: (c, onChange) => <TrueFalseEditor content={c} onChange={onChange} />,
  },
  {
    // Вариант того же типа: две кнопки, без «Not Given» (lib/trueFalse.ts).
    type: 'true_false_ng',
    key: 'true_false',
    labelKey: 'blockRegistry.lblTrueFalse',
    icon: 'rule',
    category: 'quiz',
    initialContent: () => ({ statements: [{ text: '', answer: 'true' }], explanation: '', not_given: false }),
    renderEditor: (c, onChange) => <TrueFalseEditor content={c} onChange={onChange} />,
  },

  // ----- Order / matching -----
  {
    type: 'sentence_order',
    labelKey: 'blockRegistry.lblBuildSentence',
    icon: 'sort',
    category: 'order',
    initialContent: () => ({ words: [''] }),
    renderEditor: (c, onChange) => (
      <RepeatableEditor
        content={c} onChange={onChange} field="sentences" reorder
        legacyKeys={['words']}
        toItem={(x) => ({ words: (x.words as string[]) ?? [''] })}
        emptyItem={() => ({ words: [''] })}
        addLabel={i18n.t('blockRegistry.addSentence2')}
        itemLabel={(i, total) => (total > 1 ? i18n.t('blockRegistry.itemSentence', { i: i + 1, total }) : i18n.t('blockRegistry.oneSentence'))}
        renderItem={(item, setItem) => <WordTokensEditor content={item} onChange={setItem} />}
      />
    ),
  },
  {
    type: 'column_sort',
    labelKey: 'blockRegistry.lblColumns',
    icon: 'view_column',
    category: 'order',
    initialContent: () => ({ columns: [{ label: '', items: [] as string[] }] }),
    renderEditor: (c, onChange) => <ColumnSortEditor content={c} onChange={onChange} />,
  },
  {
    type: 'paragraph_order',
    labelKey: 'blockRegistry.lblOrderParas',
    icon: 'reorder',
    category: 'order',
    initialContent: () => ({ paragraphs: [''] }),
    renderEditor: (c, onChange) => <SentenceRowsEditor content={c} onChange={onChange} />,
  },
  {
    type: 'anagram',
    labelKey: 'blockRegistry.lblAnagram',
    icon: 'spellcheck',
    category: 'order',
    initialContent: () => ({ word: '', clue: '' }),
    renderEditor: (c, onChange) => (
      <RepeatableEditor
        content={c} onChange={onChange} field="words" reorder
        legacyKeys={['word', 'clue']}
        toItem={(x) => ({ word: (x.word as string) ?? '', clue: (x.clue as string) ?? '' })}
        emptyItem={() => ({ word: '', clue: '' })}
        addLabel={i18n.t('blockRegistry.addWord2')}
        itemLabel={(i, total) => (total > 1 ? i18n.t('blockRegistry.itemWord', { i: i + 1, total }) : i18n.t('blockRegistry.oneWord'))}
        renderItem={(item, setItem) => (
          <div className="space-y-2">
            <SimpleInput value={(item.word as string) ?? ''} onChange={(v) => setItem({ ...item, word: v })} placeholder={i18n.t('blockRegistry.phTargetWord')} />
            <SimpleInput value={(item.clue as string) ?? ''} onChange={(v) => setItem({ ...item, clue: v })} placeholder={i18n.t('blockRegistry.phClueOptional')} />
          </div>
        )}
      />
    ),
  },
  {
    type: 'matching',
    labelKey: 'blockRegistry.lblMatch',
    icon: 'compare_arrows',
    category: 'order',
    initialContent: () => ({ pairs: [{ left: '', right: '' }] }),
    renderEditor: (c, onChange) => <MatchingEditor content={c} onChange={onChange} />,
  },

  // ----- Text -----
  {
    type: 'article',
    labelKey: 'blockRegistry.lblArticle',
    icon: 'article',
    category: 'text',
    initialContent: () => ({ image_url: '', heading: '', body: '' }),
    renderEditor: (c, onChange) => (
      <div className="space-y-2">
        <UploadField
          value={(c.image_url as string) ?? ''}
          onChange={(v) => onChange({ ...c, image_url: v })}
          placeholder={i18n.t('blockRegistry.phPicUrlOptional')}
          accept="image/*"
        />
        <SimpleInput value={(c.heading as string) ?? ''} onChange={(v) => onChange({ ...c, heading: v })} placeholder={i18n.t('blockRegistry.phHeading')} />
        <RichTextEditor
          minRows={5}
          value={(c.body as string) ?? ''}
          onChange={(v) => onChange({ ...c, body: v })}
          placeholder={i18n.t('blockRegistry.phArticleText')}
        />
      </div>
    ),
  },
  {
    type: 'text',
    labelKey: 'blockRegistry.lblRichText',
    icon: 'description',
    category: 'text',
    initialContent: () => ({ html: '' }),
    renderEditor: (c, onChange) => (
      <RichTextEditor
        minRows={4}
        value={(c.html as string) ?? ''}
        onChange={(v) => onChange({ ...c, html: v })}
        placeholder={i18n.t('blockRegistry.phLessonText')}
      />
    ),
  },
  {
    type: 'essay',
    labelKey: 'blockRegistry.lblEssay',
    icon: 'edit_document',
    category: 'text',
    // Default: NO minimum (0). The curator sets a per-task limit if they want one.
    initialContent: () => ({ prompt: '', min_words: 0 }),
    renderEditor: (c, onChange) => (
      <div className="space-y-2">
        <RichTextEditor value={(c.prompt as string) ?? ''} onChange={(v) => onChange({ ...c, prompt: v })} placeholder={i18n.t('blockRegistry.phTask')} />
        <SimpleInput
          value={String((c.min_words as number) ?? 0)}
          onChange={(v) => onChange({ ...c, min_words: Number(v) || 0 })}
          placeholder={i18n.t('blockRegistry.phMinWords')}
        />
      </div>
    ),
  },
  {
    // «Ответ по пунктам» (13.10.2026): открытое задание, проверяет куратор; lib/openItems.ts.
    type: 'open_items',
    labelKey: 'blockRegistry.lblOpenItems',
    icon: 'fact_check',
    category: 'text',
    initialContent: () => ({ instruction: '', items: [{ text: '', hint: '', sample: '' }] }),
    renderEditor: (c, onChange) => (
      <div className="space-y-2">
        <RichTextEditor
          value={(c.instruction as string) ?? ''}
          onChange={(v) => onChange({ ...c, instruction: v })}
          placeholder={i18n.t('blockRegistry.phTask')}
        />
        <OpenItemsEditor content={c} onChange={onChange} />
      </div>
    ),
  },

  // ----- Misc -----
  {
    type: 'word_card_set',
    labelKey: 'blockRegistry.lblVocabSet',
    icon: 'translate',
    category: 'misc',
    initialContent: () => ({ words: [{ word: '', translation: '', example: '' }], voice: 'en-US' }),
    renderEditor: (c, onChange) => <WordCardSetEditor content={c} onChange={onChange} />,
  },
  {
    type: 'callout',
    labelKey: 'blockRegistry.lblCallout',
    icon: 'campaign',
    category: 'misc',
    initialContent: () => ({ level: 'info', text: '' }),
    renderEditor: (c, onChange) => (
      <div className="space-y-2">
        <select
          value={(c.level as string) ?? 'info'}
          onChange={(e) => onChange({ ...c, level: e.target.value })}
          className="rounded-button bg-surface-container-low p-3 text-on-surface focus:outline-none"
        >
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="tip">Tip</option>
        </select>
        <RichTextEditor value={(c.text as string) ?? ''} onChange={(v) => onChange({ ...c, text: v })} placeholder={i18n.t('blockRegistry.phNoteText')} />
      </div>
    ),
  },
  {
    type: 'link',
    labelKey: 'blockRegistry.lblLink',
    icon: 'link',
    category: 'misc',
    initialContent: () => ({ url: '', label: '' }),
    renderEditor: (c, onChange) => (
      <div className="space-y-2">
        <UploadField value={(c.url as string) ?? ''} onChange={(v) => onChange({ ...c, url: v })} placeholder={i18n.t('blockRegistry.phUrlOrFile')} accept="*/*" buttonLabel={i18n.t('blockRegistry.uploadFile')} />
        <SimpleInput value={(c.label as string) ?? ''} onChange={(v) => onChange({ ...c, label: v })} placeholder={i18n.t('blockRegistry.phButtonText')} />
      </div>
    ),
  },
  {
    type: 'divider',
    labelKey: 'blockRegistry.lblDivider',
    icon: 'horizontal_rule',
    category: 'misc',
    initialContent: () => ({}),
    renderEditor: () => <hr className="my-2 border-outline-variant" />,
  },
  {
    // Universal iframe embed — one block covers Wordwall, LearningApps, Miro,
    // H5P, YouTube-embed, Genially, etc. Paste the provider's embed URL.
    type: 'embed',
    labelKey: 'blockRegistry.lblEmbed',
    icon: 'integration_instructions',
    category: 'misc',
    initialContent: () => ({ url: '', height: 400 }),
    renderEditor: (c, onChange) => (
      <div className="space-y-2">
        <SimpleInput value={(c.url as string) ?? ''} onChange={(v) => onChange({ ...c, url: v })} placeholder={i18n.t('blockRegistry.phEmbedUrl')} />
        <SimpleInput value={String((c.height as number) ?? 400)} onChange={(v) => onChange({ ...c, height: Number(v) || 400 })} placeholder={i18n.t('blockRegistry.phHeightPx')} />
      </div>
    ),
  },
  {
    // Highlight-the-words: author writes text with the CORRECT words wrapped in
    // [square brackets]; the student clicks words to select the right ones.
    type: 'highlight',
    labelKey: 'blockRegistry.lblHighlight',
    icon: 'ink_highlighter',
    category: 'fill',
    initialContent: () => ({ text: '' }),
    renderEditor: (c, onChange) => (
      <div className="space-y-1">
        <SimpleTextEdit
          value={(c.text as string) ?? ''}
          onChange={(v) => onChange({ ...c, text: v })}
          placeholder={i18n.t('blockRegistry.phHighlightText')}
          rows={4}
        />
        <p className="text-xs text-on-surface-variant">{i18n.t('blockRegistry.highlightHint')}</p>
      </div>
    ),
  },
  {
    type: 'pdf',
    labelKey: 'blockRegistry.lblPdf',
    icon: 'picture_as_pdf',
    category: 'media',
    initialContent: () => ({ url: '', title: '' }),
    renderEditor: (c, onChange) => (
      <div className="space-y-2">
        <UploadField value={(c.url as string) ?? ''} onChange={(v) => onChange({ ...c, url: v })} placeholder={i18n.t('blockRegistry.phPdfUrl')} accept="application/pdf" buttonLabel={i18n.t('blockRegistry.uploadPdf')} />
        {/* ⚠️ СВОЕГО ПОЛЯ «НАЗВАНИЕ PDF» БОЛЬШЕ НЕТ (13.09.2026), как раньше у видео. Оно
            писало в тот же `content.title`, что и общее поле «Заголовок», а тот теперь
            с форматированием: простое поле показало бы методисту теги. */}
      </div>
    ),
  },
];

export const blockRegistryByType: Record<BlockType, BlockDef> = Object.fromEntries(
  blockRegistry.filter((b) => !b.key).map((b) => [b.type, b]),
) as Record<BlockType, BlockDef>;

/** Плитка палитры: её ключ (вариант) или тип. */
export const paletteKey = (b: BlockDef): string => b.key ?? b.type;

/** Запись реестра по плитке палитры; неизвестная — по типу. */
export const blockDefByPick = (pick: string): BlockDef =>
  blockRegistry.find((b) => paletteKey(b) === pick) ?? blockRegistryByType[pick as BlockType];

export function useBlockCategories(): { id: BlockCategory; label: string }[] {
  const { t } = useTranslation();
  return [
    { id: 'media', label: t('blockRegistry.catMedia') },
    { id: 'fill', label: t('blockRegistry.catFill') },
    { id: 'quiz', label: t('blockRegistry.catQuiz') },
    { id: 'order', label: t('blockRegistry.catOrder') },
    { id: 'text', label: t('blockRegistry.catText') },
    { id: 'misc', label: t('blockRegistry.catMisc') },
  ];
}
